import { LangToggle } from "@/components/LangToggle";
import {
  createFileRoute,
  Link,
  Outlet,
  useRouterState,
  retainSearchParams,
} from "@tanstack/react-router";
import {
  LayoutDashboard,
  Wand2,
  Users,
  Settings,
  ExternalLink,
  Menu,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useI18n } from "@/lib/i18n";
import { Logo } from "@/components/Logo";
import { cn } from "@/lib/utils";
import { brand } from "@/lib/brand";
import { useCustomerStore } from "@/stores/customerStore";
import { ErrorState, LoadingState } from "@/components/DataState";
import { Button } from "@/components/ui/button";
import { useDiscoveryHost } from "@/components/discovery-host-context";
import { watchCustomerAccess, type CustomerAccess } from "@/lib/access-control";
import { DashboardAccessProvider } from "@/components/dashboard-access-context";
import { AccountMenu } from "@/components/AccountMenu";
import { AdminGuard } from "@/components/AdminGuard";
import { useAuth } from "@/auth/AuthProvider";
import { useSystemStore } from "@/stores/systemStore";
import { AppError } from "@/data/errors";
import { useInitialSplashReady } from "@/hooks/useInitialSplashReady";

export const Route = createFileRoute("/app/$customerId")({
  validateSearch: (search: Record<string, unknown>): { adminView?: boolean } => search["adminView"] === true || search["adminView"] === "true" ? { adminView: true } : {},
  search: { middlewares: [retainSearchParams(["adminView"])] },
  component: AppLayout,
});

const groups = [
  {
    label: "nav.section.workspace",
    items: [
      { to: "/app/$customerId", key: "nav.dashboard", icon: LayoutDashboard, exact: true },
      { to: "/app/$customerId/leads", key: "nav.leads", icon: Users },
    ],
  },
  {
    label: "nav.section.intelligence",
    items: [
      { to: "/app/$customerId/journey", key: "nav.journey", icon: Wand2 },
      { to: "/app/$customerId/settings", key: "nav.settings", icon: Settings },
    ],
  },
];

function AppLayout() {
  const { adminView } = Route.useSearch();
  return adminView ? <AdminGuard><Workspace key="admin" adminView /></AdminGuard> : <Workspace key="customer" />;
}

function Workspace({ adminView = false }: { adminView?: boolean }) {
  const { customerId } = Route.useParams();
  const { currentUser, authLoading } = useAuth();
  const { uid: systemUid, adminAccessResolved, isSystemAdmin } = useSystemStore();
  const publicLinkReady = !authLoading && (!currentUser || (systemUid === currentUser.uid && adminAccessResolved));
  const sameTabPublic = !currentUser || (systemUid === currentUser.uid && isSystemAdmin);
  const { openDiscovery } = useDiscoveryHost();
  const { t, tl } = useI18n();
  const [open, setOpen] = useState(false);
  const [customerAccess, setAccess] = useState<CustomerAccess & { customerId?: string }>({ mode: "read-only", userId: null, pending: true, canRead: false });
  const access = customerAccess;
  const identityMatches = !authLoading && access.userId === (currentUser && !currentUser.isAnonymous ? currentUser.uid : null);
  const readable = access.customerId === customerId && identityMatches && !access.pending && access.canRead !== false;
  const path = useRouterState({ select: (s) => s.location.pathname });
  const isJourney = path.endsWith("/journey");
  const settings = useCustomerStore((state) => state.settings);
  const status = useCustomerStore((state) => state.status);
  const error = useCustomerStore((state) => state.error);
  const accessSettled = identityMatches && access.customerId === customerId && !access.pending;
  const workspaceSettled = status !== "loading" && status !== "idle";
  const isDashboard = path.replace(/\/$/, "") === `/app/${customerId}`;
  // Overview signals after its own data resolves. Terminal errors also reveal.
  useInitialSplashReady(accessSettled && (!readable || (workspaceSettled &&
    (status === "error" || !settings || !isDashboard))));

  useEffect(() => {
    if (!readable) { useCustomerStore.getState().stop(); return; }
    useCustomerStore.getState().start(customerId);
    return () => useCustomerStore.getState().stop();
  }, [customerId, adminView, readable, access.userId]);

  useEffect(() => watchCustomerAccess(customerId, (next) => setAccess({ ...next, customerId })), [customerId]);

  if (!identityMatches || access.customerId !== customerId || access.pending) {
    return <div className="app-canvas min-h-screen p-6"><LoadingState label="Loading workspace..." /></div>;
  }
  if (!readable) {
    return <div className="app-canvas min-h-screen p-6"><ErrorState error={new AppError("access", "You do not have access to this workspace.")} /><AccountMenu /></div>;
  }

  if (status === "loading" || status === "idle") {
    return <div className="app-canvas min-h-screen p-6"><LoadingState label="Loading workspace…" /></div>;
  }

  if (status === "error" || !settings) {
    return <div className="app-canvas min-h-screen p-6"><ErrorState error={error} /></div>;
  }

  const business = settings.profile;

  const nav = (
    <nav className="flex h-full flex-col gap-6 p-4">
      <div className="px-2 pt-1">
        {publicLinkReady ? <a href={sameTabPublic ? "/" : "/?view=public"} target={sameTabPublic ? undefined : "_blank"} rel={sameTabPublic ? undefined : "noopener noreferrer"} aria-label="Visit Qotta website" className="inline-block focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"><Logo size="default" /></a> : <Logo size="default" />}
        <a
          href={brand.url}
          className="mt-1 block w-fit text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          {brand.domain}
        </a>
      </div>

      <div className="brand-panel flex items-center gap-2.5 p-3 shadow-none">
        <span className="mesh-gradient grid size-9 shrink-0 place-items-center rounded-xl text-xs font-bold text-primary-foreground shadow-sm">
          CA
        </span>
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold">
            {tl(business.name)}
          </span>
          <span className="block truncate text-[11px] text-muted-foreground">
            {business.website}
          </span>
        </span>
      </div>

      <div className="flex-1 space-y-6">
        {groups.map((group) => (
          <div key={group.label}>
            <p className="px-3 pb-2 text-[10px] font-bold tracking-widest text-muted-foreground uppercase">
              {t(group.label)}
            </p>
            <ul className="space-y-0.5">
              {group.items.map((item) => {
                const target = item.to.replace("$customerId", customerId);
                const active = item.exact
                  ? path === target
                  : path.startsWith(target);
                return (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      params={{ customerId }}
                      onClick={() => setOpen(false)}
                      className={cn(
                        "flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200",
                        active
                          ? "bg-primary-soft text-primary shadow-sm ring-1 ring-primary/10"
                          : "text-muted-foreground hover:bg-primary-soft/55 hover:text-primary",
                      )}
                    >
                      <item.icon className="size-4 shrink-0" aria-hidden />
                      <span className="truncate">{t(item.key)}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>

      {adminView ? <button
        type="button"
        onClick={() => openDiscovery({ publicCustomerSlug: settings.publicConfig.publicSlug, mode: "live", adminView: true })}
        className="discovery-panel flex items-center gap-2 p-3 text-sm font-bold"
      ><ExternalLink className="size-4" />{t("nav.viewLive")}</button> : <button
        type="button"
        onClick={() =>
          openDiscovery({
            publicCustomerSlug: settings.publicConfig.publicSlug,
            mode: "live",
          })
        }
        className="lift discovery-panel flex w-full items-center gap-2 p-3 text-start text-sm font-bold text-discovery-foreground shadow-none"
      >
        <ExternalLink className="size-4 shrink-0" aria-hidden />
        <span className="truncate">{t("nav.viewLive")}</span>
      </button>}
    </nav>
  );

  return (
    <DashboardAccessProvider value={access}>
    <div
      className="app-canvas min-h-screen"
      data-testid="owner-workspace"
      data-customer-id={customerId}
      data-access-mode={access.mode}
      data-admin-view={adminView || undefined}
    >
      <aside className="fixed inset-y-0 start-0 z-40 hidden w-64 border-e border-sidebar-border bg-sidebar/92 shadow-sidebar backdrop-blur-xl lg:block">
        {nav}
      </aside>

      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            aria-label={t("common.close")}
            className="absolute inset-0 bg-foreground/20 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />
          <div className="animate-slide-in absolute inset-y-0 start-0 w-72 border-e border-sidebar-border bg-sidebar shadow-elevated">
            {nav}
          </div>
        </div>
      )}

      <div className="lg:ps-64">
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-border bg-surface-elevated/85 px-4 py-3 shadow-header backdrop-blur-xl sm:px-8">
          <button
            onClick={() => setOpen((v) => !v)}
            className="grid size-10 shrink-0 place-items-center rounded-xl border border-border bg-card shadow-sm transition-colors hover:bg-primary-soft hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:hidden"
            aria-label="Menu"
          >
            {open ? (
              <X className="size-4" aria-hidden />
            ) : (
              <Menu className="size-4" aria-hidden />
            )}
          </button>
          <div className="ms-auto flex items-center gap-2">
            <span className="app-chip hidden border-success/15 bg-success-soft px-2.5 py-1 text-[11px] font-bold text-success sm:inline-flex">
              {access.mode === "manage" ? "Manage" : "Read-only preview"}
            </span>
            <LangToggle />
            <AccountMenu />
          </div>
        </header>

        <main className={cn("relative mx-auto px-4 pb-16 pt-8 sm:px-8", isJourney ? "max-w-[1600px]" : "max-w-6xl")}>
          {adminView && <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/20 bg-primary-soft p-3 text-sm"><span>Admin Dashboard</span><Link to="/admin/companies/$customerId" params={{ customerId }} className="font-semibold text-primary">Back to company</Link></div>}
          <Outlet />
        </main>
      </div>
    </div>
    </DashboardAccessProvider>
  );
}
